import React, { useState, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import AppLayout from '@/components/layout/AppLayout';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Calendar, CheckCircle, Clock, FileText, Users, XCircle } from 'lucide-react';
import { useUser } from '@/contexts/UserContext';
import LeaveRequestForm from './components/LeaveRequestForm';
import LeaveRequestsList from './components/LeaveRequestsList';
import { AdminLeaveRequests } from './components/AdminLeaveRequests';
import ClientLeaveRequests from './components/ClientLeaveRequests';
import ClientLeaveStatsChart from './components/ClientLeaveStatsChart';
import EmployeeNonWorkingStats from './components/EmployeeNonWorkingStats';
import { ShiftSwapRequests } from '../leave-requests/components/ShiftSwapRequests';
import { AdminSwapApprovals } from '../leave-requests/components/AdminSwapApprovals';

interface LeaveStats {
  approved: number;
  pending: number;
  total: number;
}

const Leave = () => {
  const { isAdmin, hasRole } = useUser();
  const isClient = hasRole ? hasRole('client') : false;
  const [activeFilter, setActiveFilter] = useState<'all' | 'pending' | 'approved' | 'rejected'>('all');
  const queryClient = useQueryClient();

  // Fetch leave request statistics - admins see all, clients see their teams, regular users see their own
  const { data: stats, isLoading: statsLoading } = useQuery({
    queryKey: ['leave-stats', isAdmin, isClient],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return { approved: 0, pending: 0, total: 0 };

      let query = supabase.from('leave_requests').select('status');
      
      if (isAdmin) {
        // Check if super admin
        const { data: isSuperAdmin } = await supabase.rpc('is_super_admin', { user_id: user.id });
        
        if (!isSuperAdmin) {
          // Non-super admins see only requests from their admin group users
          const { data: adminGroup } = await supabase.rpc('get_admin_group', { user_id: user.id });

          if (adminGroup && adminGroup.length > 0) {
            const adminGroupIds = adminGroup.map((g: any) => g.admin_id);
            
            // Get all users created by anyone in the admin group
            const { data: groupUsers } = await supabase
              .from('profiles')
              .select('id')
              .in('created_by', adminGroupIds);

            if (groupUsers && groupUsers.length > 0) {
              const userIds = groupUsers.map(u => u.id);
              query = query.in('user_id', userIds);
            } else {
              return { approved: 0, pending: 0, total: 0 };
            }
          } else {
            return { approved: 0, pending: 0, total: 0 };
          }
        }
      } else if (isClient) {
        // Clients see requests from their assigned teams
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          const { data: clientTeams } = await supabase
            .from('client_teams')
            .select('team_id')
            .eq('client_id', user.id);
          
          if (clientTeams && clientTeams.length > 0) {
            const teamIds = clientTeams.map(t => t.team_id);
            query = query.in('team_id', teamIds);
          } else {
            // No teams assigned, return empty stats
            return { approved: 0, pending: 0, total: 0 };
          }
        }
      } else {
        // Regular users see only their own requests
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          query = query.eq('user_id', user.id);
        }
      }

      const { data, error } = await query;

      if (error) throw error;

      const stats: LeaveStats = {
        approved: data.filter(req => req.status === 'approved').length,
        pending: data.filter(req => req.status === 'pending').length,
        total: data.length
      };

      return stats;
    }
  });

  // Real-time subscription for leave stats
  useEffect(() => {
    const channel = supabase
      .channel('leave-stats-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'leave_requests'
        },
        () => {
          queryClient.invalidateQueries({ queryKey: ['leave-stats', isAdmin, isClient] });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [isAdmin, isClient, queryClient]);

  return (
    <AppLayout>
      <div className="space-y-8 animate-fadeIn px-8 py-8 bg-background min-h-screen">
        {/* Header */}
        <div className="text-center space-y-4">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-primary text-primary-foreground mb-4">
            <Calendar className="w-8 h-8" />
          </div>
          <h1 className="text-4xl font-bold text-foreground">Requests</h1>
          <p className="text-lg text-muted-foreground">
            {isAdmin ? 'Manage all organization leave requests' : isClient ? 'View team member leave requests' : 'Submit and manage requests with streamlined workflow'}
          </p>
        </div>

        {/* Statistics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card className="p-6 bg-gradient-to-br from-green-500 to-green-600 text-white relative overflow-hidden">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-medium opacity-90">Approved Requests</h3>
                <p className="text-3xl font-bold mt-2">
                  {statsLoading ? '--' : stats?.approved || 0}
                </p>
              </div>
              <CheckCircle className="w-12 h-12 opacity-80" />
            </div>
          </Card>

          <Card className="p-6 bg-gradient-to-br from-orange-500 to-orange-600 text-white relative overflow-hidden">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-medium opacity-90">Pending Reviews</h3>
                <p className="text-3xl font-bold mt-2">
                  {statsLoading ? '--' : stats?.pending || 0}
                </p>
              </div>
              <Clock className="w-12 h-12 opacity-80" />
            </div>
          </Card>

          <Card className="p-6 bg-gradient-to-br from-purple-500 to-purple-600 text-white relative overflow-hidden">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-medium opacity-90">Total Requests</h3>
                <p className="text-3xl font-bold mt-2">
                  {statsLoading ? '--' : stats?.total || 0}
                </p>
              </div>
              <FileText className="w-12 h-12 opacity-80" />
            </div>
          </Card>
        </div>

        {/* Client Leave Stats Chart or Employee Non-Working Stats */}
        {isClient ? (
          <ClientLeaveStatsChart />
        ) : (
          <EmployeeNonWorkingStats />
        )}
 
        {/* All Requests Button */}
        <div className="flex justify-center">
          <button className="inline-flex items-center gap-2 px-6 py-3 bg-primary hover:bg-primary/90 text-primary-foreground rounded-lg font-medium transition-colors">
            <Users className="w-5 h-5" />
            {isAdmin ? 'All Organization Requests' : isClient ? 'All Team Requests' : 'All Requests'}
          </button>
        </div>

        {/* All Leave Requests Section */}
        <Card className="p-6 bg-card border-border">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
              <Users className="w-5 h-5 text-primary" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-foreground">
                {isAdmin ? 'All Organization Leave Requests' : isClient ? 'Team Leave Requests' : 'My Leave Requests'}
              </h2>
              <p className="text-muted-foreground">
                {isAdmin ? 'Manage all organization leave requests' : isClient ? 'View team member leave requests' : 'View and manage your leave requests'}
              </p>
            </div>
          </div>

          {/* Status Filter Badges */}
          <div className="flex flex-wrap gap-2 mb-6 overflow-hidden">
            <Badge 
              variant={activeFilter === 'all' ? 'default' : 'outline'}
              className={`cursor-pointer px-3 py-1 flex-shrink-0 ${
                activeFilter === 'all' 
                  ? 'bg-blue-100 text-blue-700 border-blue-200' 
                  : 'hover:bg-blue-50'
              }`}
              onClick={() => setActiveFilter('all')}
            >
              <FileText className="w-3 h-3 mr-1 flex-shrink-0" />
              <span className="truncate">All</span>
            </Badge>
            <Badge 
              variant={activeFilter === 'pending' ? 'default' : 'outline'}
              className={`cursor-pointer px-3 py-1 flex-shrink-0 ${
                activeFilter === 'pending' 
                  ? 'bg-yellow-100 text-yellow-700 border-yellow-200' 
                  : 'hover:bg-yellow-50'
              }`}
              onClick={() => setActiveFilter('pending')}
            >
              <Clock className="w-3 h-3 mr-1 flex-shrink-0" />
              <span className="truncate">Pending</span>
            </Badge>
            <Badge 
              variant={activeFilter === 'approved' ? 'default' : 'outline'}
              className={`cursor-pointer px-3 py-1 flex-shrink-0 ${
                activeFilter === 'approved' 
                  ? 'bg-green-100 text-green-700 border-green-200' 
                  : 'hover:bg-green-50'
              }`}
              onClick={() => setActiveFilter('approved')}
            >
              <CheckCircle className="w-3 h-3 mr-1 flex-shrink-0" />
              <span className="truncate">Approved</span>
            </Badge>
            <Badge 
              variant={activeFilter === 'rejected' ? 'default' : 'outline'}
              className={`cursor-pointer px-3 py-1 flex-shrink-0 ${
                activeFilter === 'rejected' 
                  ? 'bg-red-100 text-red-700 border-red-200' 
                  : 'hover:bg-red-50'
              }`}
              onClick={() => setActiveFilter('rejected')}
            >
              <XCircle className="w-3 h-3 mr-1 flex-shrink-0" />
              <span className="truncate">Rejected</span>
            </Badge>
          </div>

          {/* Leave Requests Content */}
          {isAdmin ? (
            <AdminLeaveRequests statusFilter={activeFilter} />
          ) : isClient ? (
            <ClientLeaveRequests statusFilter={activeFilter} />
          ) : (
            <div className="space-y-6">
              <LeaveRequestForm />
              <LeaveRequestsList />
            </div>
          )}
        </Card>

        {/* Shift Swap Requests Section */}
        {isAdmin ? (
          <AdminSwapApprovals />
        ) : !isClient && (
          <ShiftSwapRequests />
        )}
      </div>
    </AppLayout>
  );
};

export default Leave;
